import { useEffect, useState } from "react";
import { T } from "@/components/T";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import Seo from "@/components/Seo";
import ForumList from "@/components/ForumList";
import InviteButton from "@/components/InviteButton";
import { fetchGroup, joinGroup, chantGroup } from "@/lib/api";
import { catMeta } from "@/lib/groupCats";
import { Users, CircleDot, Loader2, ArrowLeft, MapPin, Sparkles, Trophy } from "lucide-react";

export default function GroupDetail() {
  const { id } = useParams();
  const { pick } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [g, setG] = useState(null);
  const [err, setErr] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => fetchGroup(id).then(setG).catch(() => setErr(true));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (err) return <div className="mx-auto max-w-2xl px-5 py-24 text-center text-ink-2">{pick("यह समूह नहीं मिला।", "This group could not be found.")}</div>;
  if (!g) return <div className="flex items-center gap-2 px-5 py-24 text-ink-3"><Loader2 className="h-4 w-4 animate-spin" /> <T>Loading…</T></div>;

  const cm = catMeta(g.category);
  const isNama = g.category === "namasmarana";
  const pct = g.target ? Math.min(100, Math.round((g.total / g.target) * 100)) : null;

  const join = async () => {
    if (!user) return navigate("/login");
    const updated = await joinGroup(g.id); setG(updated);
    toast(pick("सम्मिलित हुए", "Joined"), { description: g.name });
  };
  const chant = async () => {
    if (!user) return navigate("/login");
    setBusy(true);
    try { const updated = await chantGroup(g.id, 108); setG(updated); toast("+108 japa", { description: g.name }); }
    catch { toast(pick("दर्ज नहीं हुआ", "Could not record japa")); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="group-detail-page" className="mx-auto max-w-5xl px-5 py-10 md:px-8">
      <Seo path={`/groups/${id}`} title={`${g.name} — Group on Dharmartha`} description={g.description || "A community group on Dharmartha."} />
      <Link to="/groups" className="inline-flex items-center gap-1.5 text-sm text-ink-2 transition-colors hover:text-saffron"><ArrowLeft className="h-4 w-4" /> {pick("सभी समूह", "All groups")}</Link>

      {/* Header */}
      <div className="mt-5 overflow-hidden border border-line bg-surface">
        <div className="relative h-40 w-full overflow-hidden md:h-48" style={{ backgroundColor: `${cm.accent}14` }}>
          {g.image_url ? <img src={g.image_url} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center font-deva text-6xl" style={{ color: cm.accent }}>{isNama ? g.mantra_deva?.[0] : (g.deity || g.name)?.[0]}</div>}
          <span className="absolute left-3 top-3 rounded-sm px-2.5 py-1 text-[11px] font-medium uppercase tracking-wider text-white" style={{ backgroundColor: cm.accent }}>{pick(cm.deva, cm.en)}</span>
        </div>
        <div className="p-5 md:p-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <h1 className="font-serif-latin text-2xl text-ink md:text-3xl">{g.name}</h1>
              {isNama && g.mantra_deva && <p className="mt-1 font-deva text-2xl text-saffron deva-lh">{g.mantra_deva}</p>}
              {isNama && g.mantra_en && <p className="text-sm italic text-ink-3">{g.mantra_en}</p>}
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-3">
                <span className="inline-flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> {g.member_count} {pick("साधक", "members")}</span>
                {g.deity && <span className="inline-flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5 text-gold" /> {g.deity}</span>}
                {g.region && <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-gold" /> {g.region}</span>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isNama && <button onClick={chant} disabled={busy} data-testid="group-detail-chant" className="inline-flex items-center gap-1.5 bg-[var(--saffron)] px-4 py-2 text-sm font-medium text-white disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CircleDot className="h-4 w-4" />} <T>+1 mala (108)</T></button>}
              {!g.joined && <button onClick={join} data-testid="group-detail-join" className="border border-line px-4 py-2 text-sm text-ink-2 hover:border-[var(--ink)] hover:text-ink">{pick("सम्मिलित हों", "Join")}</button>}
              <InviteButton context={g.name} testid="group-invite" />
            </div>
          </div>
          {g.description && <p className="mt-3 text-sm leading-relaxed text-ink-2">{g.description}</p>}
          {isNama && (
            <div className="mt-4 border-t border-line pt-4">
              <div className="flex items-end justify-between">
                <div><p className="font-serif-latin text-3xl text-ink" data-testid="group-detail-total">{(g.total || 0).toLocaleString()}</p><p className="text-[11px] uppercase tracking-wider text-ink-3">{pick("कुल जप", "total japa")}</p></div>
                {pct !== null && <p className="text-xs text-ink-3">{pct}% {pick("लक्ष्य का", "of")} {g.target?.toLocaleString()}</p>}
              </div>
              {pct !== null && <div className="mt-2 h-1.5 w-full overflow-hidden bg-line"><div className="h-full bg-[var(--gold)]" style={{ width: `${pct}%` }} /></div>}
            </div>
          )}
        </div>
      </div>

      {/* Members leaderboard */}
      {g.members?.length > 0 && (
        <div className="mt-6 border border-line bg-surface p-5">
          <p className="inline-flex items-center gap-1.5 font-serif-latin text-lg text-ink"><Trophy className="h-4 w-4 text-gold" /> {pick("सदस्य", "Members")}</p>
          <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1.5 text-sm text-ink-2">
            {g.members.slice(0, 20).map((m, i) => (
              <span key={i} data-testid={`group-member-${i}`}>{i + 1}. {m.name}{isNama ? ` · ${m.count}` : ""}</span>
            ))}
          </div>
        </div>
      )}

      {/* In-group discussion */}
      <div className="mt-8">
        <h2 className="border-b border-line pb-2 font-serif-latin text-xl text-ink">{pick("समूह चर्चा", "Group discussion")}</h2>
        <div className="mt-4">
          <ForumList kind="discussion" basePath={`/groups/${id}`} groupId={id} />
        </div>
      </div>
    </div>
  );
}
